/**
 * Module dependencies.
 */

import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'src/components/core/modal';

/**
 * `Props` type.
 */

type Props = {
  shouldConfirmLeave: boolean;
};

/**
 * Export `UnsavedChangesDialog` component.
 */

export function UnsavedChangesDialog({ shouldConfirmLeave }: Props) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nextRoute, setNextRoute] = useState<string | undefined>();
  const router = useRouter();
  const onRouteChangeStart = useCallback(
    (nextUrl: string) => {
      if (!shouldConfirmLeave) {
        return;
      }

      setIsModalOpen(true);
      setNextRoute(nextUrl);
      router.events.emit('routeChangeError');

      // Throwing an error will cancel the route change.
      // eslint-disable-next-line no-throw-literal
      throw 'cancelRouteChange';
    },
    [router.events, shouldConfirmLeave]
  );

  const removeListener = useCallback(() => {
    router.events.off('routeChangeStart', onRouteChangeStart);
  }, [onRouteChangeStart, router.events]);

  const onRejectRouteChange = useCallback(() => {
    setNextRoute(undefined);
    setIsModalOpen(false);
  }, []);

  const onConfirmRouteChange = useCallback(() => {
    setIsModalOpen(false);
    removeListener();
    router.push(nextRoute as string);
  }, [removeListener, router, nextRoute]);

  useEffect(() => {
    router.events.on('routeChangeStart', onRouteChangeStart);

    return removeListener;
  }, [onRouteChangeStart, removeListener, router.events]);

  return (
    <Modal
      actions={[
        {
          label: t('common:actions.cancel'),
          onClick: onRejectRouteChange
        },
        {
          label: t('common:actions.quit'),
          onClick: onConfirmRouteChange
        }
      ]}
      description={t('common:modal.formUnsavedChanges.description')}
      isOpen={isModalOpen}
      onRequestClose={onRejectRouteChange}
      title={t('common:modal.formUnsavedChanges.title')}
    />
  );
}
